/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React from 'react';
import { Platform, TouchableOpacity, View } from 'react-native';

import ButtonView, {
  ButtonViewState,
} from '@airasia-phoenix/react-native/components/ButtonView';
import CheckBoxView from '@airasia-phoenix/react-native/components/CheckBoxView';
import DateTimeFieldView, {
  FieldType,
} from '@airasia-phoenix/react-native/components/DateTimeFieldView';
import NotificationView from '@airasia-phoenix/react-native/components/NotificationView';
import RadioButtonGroupView from '@airasia-phoenix/react-native/components/RadioButtonGroupView';
import { getColor, pd } from '@airasia-phoenix/react-native/utils/utils';
import { getCurrentBreakpoint } from '@airasia-rn/ui/utils';
import { DialingCodeDropdownFieldView } from '../../components/DialingCodeDropdownFieldView';
import { DateUtils } from '../../utils/dateUtils';
import { withPlusDialingCode } from '../../utils/phoneUtils';
import styles from './styles';
import { defaultGenderRadioGroupProps, SignUpFormProps } from './types';
import { useSignUpFormView } from './useSignUpFormView';
import ArrowDropDown from '@move-travel/react-native-phoenix-icons/ArrowDropDown';

import TextFieldView from '@airasia-phoenix/react-native/components/TextFieldView';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import MobileFieldView from '@airasia-phoenix/react-native/components/MobileFieldView';
import TextView from '@airasia-phoenix/react-native/components/TextView';

export default function SignUpFormView(props: SignUpFormProps) {
  const {
    t,
    headings,
    genderLabelProps,
    genderErrorProps,
    givenNameProps,
    surNameProps,
    primaryButtonProps,
    mobileFieldProps,
    isDialingCodeDisabled,
    dobProps,
    onCloseIconClick,
    givenName,
    surName,
    dob,
    mobileLogin,
    countrySearch,
    setCountrySearch,
    hasParentalConsent,
    notification,
    givenNameErrorMessage,
    surNameErrorMessage,
    dobError,
    genderError,
    mobileErrorMessage,
    dialingCodeErrorMessage,
    isGivenNameFocused,
    setIsGivenNameFocused,
    isSurNameFocused,
    setIsSurNameFocused,
    genderOptions,
    filteredCountryData,
    indexValues,
    showParentalConsent,
    parentalConsentLabel,
    parentalConsentLabelWidth,
    isContinueDisabled,
    isApiLoading,
    mobileFieldRef,
    getFilledFieldEndIcon,
    handleGivenNameChange,
    handleSurNameChange,
    handleDobChange,
    handleDobPickerSheetStateChange,
    handleGenderChange,
    handleMobileNumberChange,
    handleDialingCodeChange,
    handleCountrySelect,
    toggleParentalConsent,
    onParentalConsentLabelLayout,
    onContinuePress,
  } = useSignUpFormView(props);
  // The country bottom sheet does not fit inside the desktop dialog, so desktop
  // web uses the same inline list as login with mobile number.
  const isDesktopWeb = Platform.OS === 'web' && getCurrentBreakpoint() > 1;

  return (
    <View style={styles.mainContainer}>
      <View style={styles.rowContainer}>
        <TouchableOpacity onPress={onCloseIconClick} testID="close-button">
          <IconView {...headings.closeIcon} />
        </TouchableOpacity>
        <TextView
          {...headings.greeting}
          text={t('signUpFormView.greeting')}
          testID="greeting-text"
        />
      </View>

      {notification && (
        <NotificationView
          style={notification.style}
          text={notification.text}
          width="100%"
        />
      )}

      <TextFieldView
        {...givenNameProps}
        label={t('signUpFormView.givenNameLabel')}
        value={givenName}
        isError={!!givenNameErrorMessage}
        errorMessage={givenNameErrorMessage}
        endIcon={getFilledFieldEndIcon(
          givenName,
          givenNameErrorMessage,
          isGivenNameFocused,
        )}
        isFocused={setIsGivenNameFocused}
        onChangeText={handleGivenNameChange}
      />

      <TextFieldView
        {...surNameProps}
        label={t('signUpFormView.surnameLabel')}
        value={surName}
        isError={!!surNameErrorMessage}
        errorMessage={surNameErrorMessage}
        endIcon={getFilledFieldEndIcon(
          surName,
          surNameErrorMessage,
          isSurNameFocused,
        )}
        isFocused={setIsSurNameFocused}
        onChangeText={handleSurNameChange}
      />
      <View style={styles.genderSection}>
        <TextView
          {...genderLabelProps}
          text={t('signUpFormView.genderLabel')}
          style={[
            genderLabelProps.style,
            genderError && { color: getColor(pd.sys.colour.sentiment_high) },
          ]}
        />
        <View style={styles.genderRadioGroup}>
          <RadioButtonGroupView
            {...defaultGenderRadioGroupProps}
            radioButtonOption={genderOptions}
            isError={!!genderError}
            onChange={handleGenderChange}
          />
        </View>
        {genderError && <TextView {...genderErrorProps} text={genderError} />}
      </View>
      {Platform.OS === 'web' ? (
        <DateTimeFieldView
          isError={dobError ? true : undefined}
          maximumValue={DateUtils.getEndOfToday()}
          fieldType={FieldType.DATE_INPUT}
          label={t('signUpFormView.dobLabel')}
          displayFormat="DD/MM/YYYY"
          disabled={false}
          errorMessage={dobError}
          onChangeText={handleDobChange}
        />
      ) : (
        <DateTimeFieldView
          {...dobProps}
          label={t('signUpFormView.dobLabel')}
          value={dob}
          maximumValue={DateUtils.getEndOfToday()}
          isError={dobError ? true : undefined}
          errorMessage={dobError}
          onChangeText={handleDobChange}
          stickyBottomBarViewProps={{
            primaryButtonLabel: t('signUpFormView.saveButton'),
            secondaryButtonLabel: t('signUpFormView.cancelButton'),
          }}
          bottomSheetViewProps={{
            showHandle: false,
            showHeader: true,
            children: undefined,
            initialHeight: 330,
            finalHeight: 330,
            sectionTitle: t('signUpFormView.dobLabel'),
            onStateChange: handleDobPickerSheetStateChange,
          }}
        />
      )}

      {isDesktopWeb ? (
        <View style={styles.desktopMobileRow}>
          <View style={styles.dialingCodeFlexStyle}>
            <DialingCodeDropdownFieldView
              testID="signup-dialing-code"
              label={t('signUpFormView.dialCodeLabel')}
              dialingCode={mobileLogin.dialingCode}
              countrySearch={countrySearch}
              setCountrySearch={setCountrySearch}
              onDialingCodeChange={handleDialingCodeChange}
              disabled={isDialingCodeDisabled ?? false}
              isError={!!dialingCodeErrorMessage}
              errorMessage={dialingCodeErrorMessage}
            />
          </View>
          <View style={styles.mobileNumberFlexStyle}>
            <TextFieldView
              testID="signup-mobile-number"
              label={t('signUpFormView.mobileNumberLabel')}
              value={mobileLogin.mobileNumber}
              onChangeText={(text) =>
                handleMobileNumberChange(text.replace(/\D/g, ''))
              }
              keyboardType="number-pad"
              disabled={false}
              isError={!!mobileErrorMessage}
              errorMessage={mobileErrorMessage}
            />
          </View>
        </View>
      ) : (
        <MobileFieldView
          {...mobileFieldProps}
          label={t('signUpFormView.mobileNumberLabel')}
          ref={mobileFieldRef}
          value={mobileLogin.mobileNumber}
          onChangeText={handleMobileNumberChange}
          isError={!!mobileErrorMessage}
          errorMessage={mobileErrorMessage}
          dialogCodeViewStyleProps={styles.dialogCodeViewStyleProps}
          mobileNumberViewStyleProps={styles.mobileNumberViewStyleProps}
          textFieldViewFlextStyleProps={{
            dialingCode: styles.dialingCodeFlexStyle,
            mobileNumber: styles.mobileNumberFlexStyle,
          }}
          dialingCodeFieldViewProps={{
            label: t('signUpFormView.dialCodeLabel'),
            value: withPlusDialingCode(mobileLogin.dialingCode),
            disabled: isDialingCodeDisabled ?? false,
            isError: !!dialingCodeErrorMessage,
            errorMessage: dialingCodeErrorMessage,
            endIcon: {
              height: 24,
              width: 24,
              tint: getColor(pd.sys.colour.content_primary),
              children: <ArrowDropDown />,
            },
            bottomSheetViewProps: {
              initialHeight: '90%',
              finalHeight: '90%',
              height: '100%',
              showHandle: true,
              showHeader: true,
              sectionTitle: t('signUpFormView.selectCountry'),
              children: undefined,
            },
            searchableListViewProps: {
              indexValues,
              textFieldViewProps: {
                label: t('signUpFormView.searchCountry'),
                value: countrySearch,
                onChangeText: setCountrySearch,
                disabled: false,
              },
              isInsideBottomSheetView: true,
              searchableListPageViewData: filteredCountryData,
              onChange: handleCountrySelect,
              isTickList: true,
            },
          }}
        />
      )}

      {showParentalConsent && (
        <View style={styles.parentalConsentRow} testID="parental-consent-row">
          <View style={styles.parentalConsentCheckbox}>
            <CheckBoxView
              isSelected={hasParentalConsent}
              onPress={toggleParentalConsent}
              testID="parental-consent-checkbox"
            />
          </View>
          <View
            style={styles.parentalConsentLabel}
            onLayout={(event) => {
              onParentalConsentLabelLayout(event.nativeEvent.layout.width);
            }}
          >
            <TextView
              {...parentalConsentLabel}
              testID="parental-consent-label"
              style={parentalConsentLabel.style}
              spannableParentTextStyle={[
                styles.parentalConsentText,
                parentalConsentLabelWidth > 0
                  ? { maxWidth: parentalConsentLabelWidth }
                  : null,
              ]}
            />
          </View>
        </View>
      )}

      <View style={styles.continueButton}>
        <ButtonView
          {...primaryButtonProps}
          text={t('signUpFormView.continueButton')}
          onPress={onContinuePress}
          testID="signup-button"
          disabled={isContinueDisabled}
          buttonViewState={
            Platform.OS === 'web' && isApiLoading
              ? ButtonViewState.LOADING
              : ButtonViewState.LOADED
          }
        />
      </View>
    </View>
  );
}
